import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toApiError } from '@/api/errorHandler';
import { PageContainer } from '@/components/Shared/PageContainer';
import { appConfig } from '@/config/appConfig';
import {
  selectedProductQueryParam,
  selectedProductStorageKey,
} from '@/constants/productSelection';
import { useInitialProductNewTabLaunch } from '@/hooks/useInitialProductNewTabLaunch';
import { useNavigationEngine } from '@/navigation/useNavigation';
import {
  collectEmbeddedNavigationItemIds,
  navigationTreeToEmbeddedGroups,
} from '@/navigation/NavigationTreeBuilder';
import { useProducts } from '@/hooks/useProducts';
import { EmbeddedApplicationLayout } from '@/layouts/EmbeddedApplicationLayout';
import { PortalLayout } from '@/layouts/PortalLayout';
import { HomeProductsSkeleton } from '@/pages/Home/HomeProductsSkeleton';
import { useAppShell } from '@/shell';
import type { EntityOption } from '@/types/entity';
import type { EmbeddedNavigationItem } from '@/types/embeddedApplication';
import { buildEmbeddedApplication } from '@/utils/buildEmbeddedApplication';
import { findSelectedItemPath } from '@/utils/embeddedNavigationPath';
import { launchProductUrlInNewTab } from '@/utils/productNewTabLaunch';
import {
  nextProductSearchParams,
  shouldApplyProductQuery,
} from '@/utils/productSelectionUrl';

const embeddedLogoutEventName = 'entity-portal:embedded-product-logout';

export function HomePage() {
  const { t } = useTranslation('entity-portal');
  const { setCurrentProductId, setBreadcrumbs } = useAppShell();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedMenuId, setSelectedMenuId] = useState<string>('');
  const [popOutHandler, setPopOutHandler] = useState<(() => void) | null>(null);

  const {
    data: products = [],
    isPending: isProductsLoading,
    isError: isProductsError,
    error: productsError,
  } = useProducts();

  const headerProducts = useMemo(() => {
    if (products.length <= 5) {
      return products;
    }

    if (!selectedProductId) {
      return products.slice(0, 5);
    }

    const selectedProduct = products.find(
      (product) => product.id === selectedProductId,
    );

    if (!selectedProduct) {
      return products.slice(0, 5);
    }

    const remainingProducts = products
      .filter((product) => product.id !== selectedProduct.id)
      .slice(0, 4);

    return [selectedProduct, ...remainingProducts];
  }, [products, selectedProductId]);

  const {
    nodes: navigationNodes,
    isLoading: isNavigationLoading,
    isError: isNavigationError,
    error: navigationError,
  } = useNavigationEngine(selectedProductId || undefined);

  const navigationGroups = useMemo(
    () => navigationTreeToEmbeddedGroups(navigationNodes),
    [navigationNodes],
  );

  const navigationItemIds = useMemo(
    () => collectEmbeddedNavigationItemIds(navigationGroups),
    [navigationGroups],
  );

  const selectedProduct = useMemo(
    () => products.find((item) => item.id === selectedProductId),
    [products, selectedProductId],
  );

  // Breadcrumb shows the full selected path (portal and product labels are shown via the entity selector).
  const breadcrumbSegments = useMemo(
    () => findSelectedItemPath(navigationGroups, selectedMenuId),
    [navigationGroups, selectedMenuId],
  );

  const embeddedApplication = useMemo(() => {
    if (!selectedProduct) {
      return null;
    }

    return buildEmbeddedApplication({
      product: selectedProduct,
      menuItems: [],
      selectedMenuItemId: selectedMenuId,
      t,
      portalLabel: appConfig.name,
      navigationGroups,
    });
  }, [navigationGroups, selectedMenuId, selectedProduct, t]);

  const iframeUrl =
    embeddedApplication?.iframeUrl ?? appConfig.defaultIframeUrl;

  const isProductContextReady =
    Boolean(selectedProductId) && products.length > 0 && !isNavigationLoading;

  useInitialProductNewTabLaunch({
    enabled: Boolean(selectedProduct?.openInNewTab),
    iframeUrl,
    isReady: isProductContextReady,
  });

  const syncProductQuery = useCallback(
    (productId: string | null) => {
      const nextParams = nextProductSearchParams(searchParams, productId);

      if (nextParams.toString() === searchParams.toString()) {
        return;
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const selectFirstAvailableProduct = useCallback(() => {
    const firstProductId = products[0]?.id ?? '';

    setSelectedProductId(firstProductId);
    window.localStorage.setItem(selectedProductStorageKey, firstProductId);
    syncProductQuery(firstProductId || null);
  }, [products, syncProductQuery]);

  const handleHeaderProductSelect = useCallback(
    (productId: string) => {
      window.localStorage.setItem(selectedProductStorageKey, productId);
      syncProductQuery(productId);

      if (productId !== selectedProductId) {
        const product = products.find((item) => item.id === productId);

        if (product?.openInNewTab) {
          launchProductUrlInNewTab(product.iframeUrl);
        }
      }

      setSelectedProductId(productId);
    },
    [products, selectedProductId, syncProductQuery],
  );

  const handleEntityChange = useCallback(
    (entity: EntityOption) => {
      handleHeaderProductSelect(entity.id);
    },
    [handleHeaderProductSelect],
  );

  const handleNavigationItemChange = useCallback(
    (menuId: string, item: EmbeddedNavigationItem) => {
      if (
        menuId !== selectedMenuId &&
        selectedProduct?.openInNewTab &&
        item.iframeUrl
      ) {
        launchProductUrlInNewTab(item.iframeUrl);
      }

      setSelectedMenuId(menuId);
    },
    [selectedMenuId, selectedProduct],
  );

  useEffect(() => {
    if (!products.length) {
      return;
    }

    const requestedProductId =
      searchParams.get(selectedProductQueryParam)?.trim() ?? '';
    const productIds = products.map((product) => product.id);

    if (
      shouldApplyProductQuery(requestedProductId, selectedProductId, productIds)
    ) {
      setSelectedProductId(requestedProductId);
      window.localStorage.setItem(
        selectedProductStorageKey,
        requestedProductId,
      );
      return;
    }

    const selectedProductStillExists = products.some(
      (product) => product.id === selectedProductId,
    );

    if (!selectedProductStillExists) {
      selectFirstAvailableProduct();
    }
  }, [products, searchParams, selectFirstAvailableProduct, selectedProductId]);

  useEffect(() => {
    const handleEmbeddedProductLogout = () => {
      selectFirstAvailableProduct();
    };

    window.addEventListener(
      embeddedLogoutEventName,
      handleEmbeddedProductLogout,
    );

    return () => {
      window.removeEventListener(
        embeddedLogoutEventName,
        handleEmbeddedProductLogout,
      );
    };
  }, [selectFirstAvailableProduct]);

  useEffect(() => {
    setCurrentProductId(selectedProductId || null);
  }, [selectedProductId, setCurrentProductId]);

  useEffect(() => {
    const productLabel = selectedProduct
      ? t(selectedProduct.name, { defaultValue: selectedProduct.name })
      : t('product.productDomainTitle');

    setBreadcrumbs([{ label: appConfig.name }, { label: productLabel }]);
  }, [selectedProduct, setBreadcrumbs, t]);

  useEffect(() => {
    if (!navigationItemIds.length) {
      setSelectedMenuId('');
      return;
    }

    const selectedMenuStillExists = navigationItemIds.includes(selectedMenuId);

    if (!selectedMenuId || !selectedMenuStillExists) {
      setSelectedMenuId(navigationItemIds[0]);
    }
  }, [navigationItemIds, selectedMenuId]);

  const headerEntities = useMemo<EntityOption[]>(
    () =>
      headerProducts.map((product) => ({
        id: product.id,
        label: t(product.name, { defaultValue: product.name }),
      })),
    [headerProducts, t],
  );

  return (
    <PortalLayout
      sidebar={null}
      showSidebar={false}
      entities={headerEntities}
      entitiesLoading={isProductsLoading}
      selectedEntityId={selectedProductId}
      onEntityChange={handleEntityChange}
      onLogoClick={selectFirstAvailableProduct}
      breadcrumbSegments={breadcrumbSegments}
      onPopOut={popOutHandler ?? undefined}
    >
      <PageContainer>
        {isProductsError ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography color="error" variant="body2">
              {toApiError(productsError, t).message}
            </Typography>
          </Box>
        ) : null}
        {isProductsLoading ? (
          <HomeProductsSkeleton />
        ) : embeddedApplication ? (
          <EmbeddedApplicationLayout
            application={embeddedApplication}
            selectedNavigationItemId={selectedMenuId}
            onNavigationItemChange={handleNavigationItemChange}
            onPopOutActionReady={(trigger) => setPopOutHandler(() => trigger)}
            isNavigationLoading={isNavigationLoading}
            navigationError={
              isNavigationError
                ? toApiError(navigationError, t).message
                : undefined
            }
          />
        ) : !isProductsError ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography color="text.secondary" variant="body2">
              {t('product.noProductsAvailable')}
            </Typography>
          </Box>
        ) : null}
      </PageContainer>
    </PortalLayout>
  );
}
